import { API_CONFIG } from "@/config/api";
import { supabase } from "@/integrations/supabase/client";
import { getAccessToken } from "@/utils/aiModels";
import { generateStudentLoginWords } from "@/utils/studentLoginWords";
import type { StudentCountry } from "@/utils/studentLoginWords";
import { z } from "zod";

// Zod schemas for type safety
const TeacherInviteSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  invite_uuid: z.string(),
  created_at: z.string(),
  used_at: z.string().nullable(),
  created_by: z.string(),
  expires_at: z.string(),
});

const CreateTeacherInviteSchema = z.object({
  email: z.string().email(),
  created_by: z.string(),
  expires_at: z.string().optional(),
  first_name: z.string().optional(),
  last_name: z.string().optional(),
});

const StudentLoginCodeSchema = z.object({
  id: z.string(),
  student_id: z.string(),
  default_password: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
});

const CreateStudentLoginCodeSchema = z.object({
  word1: z.string(),
  word2: z.string(),
  password: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  isDefaultPassword: z.boolean().optional(),
});

const BulkInviteResultSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  totalTeachers: z.number(),
  invited: z.number(),
  failed: z.number(),
  alreadyInvited: z.number(),
  details: z
    .object({
      invitedEmails: z.array(z.string()),
      failedInvites: z.array(
        z.object({
          email: z.string(),
          error: z.string(),
        })
      ),
    })
    .optional(),
});

export type TeacherInvite = z.infer<typeof TeacherInviteSchema>;
export type CreateTeacherInvite = z.infer<typeof CreateTeacherInviteSchema>;
export type StudentLoginCode = z.infer<typeof StudentLoginCodeSchema>;
export type CreateStudentLoginCode = z.infer<typeof CreateStudentLoginCodeSchema>;
export type BulkInviteResult = z.infer<typeof BulkInviteResultSchema>;

class TeacherInviteService {
  /**
   * Create a new teacher invite using Supabase's native invitation system
   * This automatically sends an email to the teacher with an invitation link
   * Uses an Edge Function to securely send the invitation with service role privileges
   * @param replaceExisting If true, will delete existing active invitations and create a new one
   */
  async createTeacherInvite(
    data: CreateTeacherInvite,
    replaceExisting: boolean = false
  ): Promise<
    | TeacherInvite
    | {
        hasActiveInvitations: true;
        activeInvitations: Array<{
          id: string;
          invite_uuid: string;
          created_at: string;
          expires_at: string;
        }>;
      }
  > {
    const validatedData = CreateTeacherInviteSchema.parse(data);

    // Get the access token for authenticated requests
    const accessToken = await getAccessToken();

    // Call the Edge Function to send invitation
    const response = await fetch(API_CONFIG.INVITE_TEACHER_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        email: validatedData.email,
        redirectTo: window.location.origin,
        replaceExisting,
        firstName: validatedData.first_name,
        lastName: validatedData.last_name,
      }),
    });

    const result = await response.json();

    // Handle case where active invitations exist
    if (response.status === 409 && result.hasActiveInvitations) {
      return {
        hasActiveInvitations: true,
        activeInvitations: result.activeInvitations,
      };
    }

    if (!response.ok) {
      throw new Error(result.error || `Failed to send teacher invite: ${response.statusText}`);
    }

    if (!result.success || !result.invite) {
      throw new Error("Failed to create teacher invite");
    }

    return TeacherInviteSchema.parse(result.invite);
  }

  /**
   * Get all teacher invites
   */
  async getTeacherInvites(): Promise<TeacherInvite[]> {
    const { data, error } = await supabase
      .from("teacher_invites")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch teacher invites: ${error.message}`);
    }

    return data.map((invite) => TeacherInviteSchema.parse(invite));
  }

  /**
   * Get teacher invite by UUID
   */
  async getTeacherInviteByUuid(inviteUuid: string): Promise<TeacherInvite | null> {
    const { data, error } = await supabase
      .from("teacher_invites")
      .select("*")
      .eq("invite_uuid", inviteUuid)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        return null; // No rows returned
      }
      throw new Error(`Failed to fetch teacher invite: ${error.message}`);
    }

    return TeacherInviteSchema.parse(data);
  }

  /**
   * Validate teacher invite
   */
  async validateTeacherInvite(inviteUuid: string): Promise<{
    is_valid: boolean;
    email: string;
    expires_at: string;
  } | null> {
    const { data, error } = await supabase.rpc("validate_teacher_invite", {
      invite_uuid_param: inviteUuid,
    });

    if (error) {
      throw new Error(`Failed to validate teacher invite: ${error.message}`);
    }

    if (data && data.length > 0) {
      return data[0];
    }

    return null;
  }

  /**
   * Mark teacher invite as used
   */
  async markTeacherInviteUsed(inviteUuid: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("mark_teacher_invite_used", {
      invite_uuid_param: inviteUuid,
      user_id_param: userId,
    });

    if (error) {
      throw new Error(`Failed to mark teacher invite as used: ${error.message}`);
    }

    return data;
  }

  /**
   * Delete teacher invite
   */
  async deleteTeacherInvite(inviteId: string): Promise<void> {
    const { error } = await supabase.from("teacher_invites").delete().eq("id", inviteId);

    if (error) {
      throw new Error(`Failed to delete teacher invite: ${error.message}`);
    }
  }

  /**
   * Bulk invite all teachers who haven't been invited yet
   * This sends invitation emails to all teachers in the database who don't have an existing invite
   */
  async bulkInviteTeachers(): Promise<BulkInviteResult> {
    // Get the access token for authenticated requests
    const accessToken = await getAccessToken();

    // Call the Edge Function to send bulk invitations
    const response = await fetch(API_CONFIG.BULK_INVITE_TEACHERS_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        redirectTo: window.location.origin,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(errorData.error || `Failed to bulk invite teachers: ${response.statusText}`);
    }

    const result = await response.json();

    return BulkInviteResultSchema.parse(result);
  }
}

class StudentLoginCodeService {
  /**
   * Create a new student login code
   */
  async createStudentLoginCode(data: CreateStudentLoginCode): Promise<StudentLoginCode> {
    const validatedData = CreateStudentLoginCodeSchema.parse(data);

    const { data: result, error } = await supabase
      .from("student_login_codes")
      .insert({
        default_password: validatedData.password,
        student_id: validatedData.word1 + "-" + validatedData.word2,
      })
      .select();

    if (error) {
      throw new Error(`Failed to create student login code: ${error.message}`);
    }
    if (!result || result.length === 0) {
      throw new Error("Failed to create student login code");
    }
    return StudentLoginCodeSchema.parse(result[0]);
  }

  /**
   * Create or update student credentials (two words + password) via Edge Function
   */
  async upsertStudentCredentials(
    word1: string,
    word2: string,
    password: string,
    firstName: string,
    lastName: string,
    isDefaultPassword: boolean = true,
    isTestStudent: boolean = false,
    country: StudentCountry = "cz",
    options?: {
      scioId?: string;
      schoolCode?: string;
      interventionGroup?: "treatment" | "control" | null;
    }
  ): Promise<{ studentId: string; created: boolean; defaultPassword: string | null }> {
    const functionsUrl = API_CONFIG.CREATE_STUDENT_CREDENTIALS_API_URL;
    const accessToken = await getAccessToken();

    const payload: Record<string, unknown> = {
      word1,
      word2,
      password,
      firstName,
      lastName,
      isDefaultPassword,
      isTestStudent,
      country,
    };
    if (options?.scioId !== undefined) payload.scioId = options.scioId;
    if (options?.schoolCode !== undefined) payload.schoolCode = options.schoolCode;
    if (options?.interventionGroup !== undefined)
      payload.interventionGroup = options.interventionGroup;

    const res = await fetch(functionsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to upsert student credentials: ${text}`);
    }

    const json = await res.json();
    if (!json?.studentId) {
      throw new Error("Edge function did not return studentId");
    }
    const { studentId, created, defaultPassword } = json;
    return { studentId, created, defaultPassword };
  }

  /**
   * Update user password (works for students, teachers, and admins)
   * Verifies the current password before updating
   */
  async updateUserPassword(
    userId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    const functionsUrl = API_CONFIG.UPDATE_STUDENT_PASSWORD_API_URL;

    try {
      const res = await fetch(functionsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ studentId: userId, currentPassword, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || "Password update failed" };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "Password update failed" };
    }
  }

  /**
   * @deprecated Use updateUserPassword instead
   * Update student password
   */
  async updateStudentPassword(
    studentId: string,
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    return this.updateUserPassword(studentId, currentPassword, newPassword);
  }

  /**
   * Get all student login codes
   */
  async getStudentLoginCodes(): Promise<any[]> {
    const { data, error } = await supabase
      .from("student_login_codes")
      .select(
        `
        *,
        students (
          id,
          word1,
          word2,
          created_at,
          updated_at,
          profiles (
            id,
            first_name,
            last_name,
            is_test_student
          )
        )
      `
      )
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch student login codes: ${error.message}`);
    }

    // Filter out test/demo students
    const filteredData = (data || []).filter(
      (code: any) => !code.students?.profiles?.is_test_student
    );

    return filteredData;
  }

  /**
   * Update student login code
   */
  async updateStudentLoginCode(
    codeId: string,
    updates: Partial<Pick<StudentLoginCode, "student_id" | "is_active">>
  ): Promise<StudentLoginCode> {
    const { data, error } = await supabase
      .from("student_login_codes")
      .update(updates)
      .eq("id", codeId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update student login code: ${error.message}`);
    }

    return StudentLoginCodeSchema.parse(data);
  }

  /**
   * Delete student login code
   */
  async deleteStudentLoginCode(codeId: string): Promise<void> {
    const { error } = await supabase.from("student_login_codes").delete().eq("id", codeId);

    if (error) {
      throw new Error(`Failed to delete student login code: ${error.message}`);
    }
  }

  /**
   * Reset student credentials (login code and password)
   * This updates an existing student's word1, word2, and password
   */
  async resetStudentCredentials(
    studentId: string,
    newWord1: string,
    newWord2: string,
    newPassword: string,
    firstName: string,
    lastName: string,
    country: StudentCountry = "cz"
  ): Promise<{ success: boolean; error?: string; defaultPassword?: string }> {
    try {
      const functionsUrl = API_CONFIG.CREATE_STUDENT_CREDENTIALS_API_URL;
      const accessToken = await getAccessToken();
      const res = await fetch(functionsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          studentId,
          word1: newWord1,
          word2: newWord2,
          password: newPassword,
          firstName,
          lastName,
          isDefaultPassword: true,
          country,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        let errorMessage = "Failed to reset student credentials";
        try {
          const errorData = JSON.parse(text);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = text || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await res.json();
      return {
        success: true,
        defaultPassword: result.defaultPassword,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || "Failed to reset student credentials",
      };
    }
  }

  /**
   * Admin-only: reset any user's password without requiring the current password.
   * Calls the admin-update-user-password Edge Function which verifies admin role.
   */
  async adminResetUserPassword(
    userId: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    const functionsUrl = API_CONFIG.ADMIN_UPDATE_USER_PASSWORD_API_URL;
    const accessToken = await getAccessToken();

    try {
      const res = await fetch(functionsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { success: false, error: data.error || "Password update failed" };
      }

      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Password update failed";
      return { success: false, error: message };
    }
  }

  /**
   * Generate a random gender-correct student login code.
   * @param country - 'cz' for Czech, 'sk' for Slovak (defaults to 'cz')
   */
  generateRandomStudentWords(country: StudentCountry = "cz"): { word1: string; word2: string } {
    return generateStudentLoginWords(country);
  }
}

// Export singleton instances
export const teacherInviteService = new TeacherInviteService();
export const studentLoginCodeService = new StudentLoginCodeService();
